<script setup lang="ts">
/**
 * Quick-add Threat Actor form.
 *
 * Minimal surface: Name + Type + Country + Notes. Everything the algorithm
 * will eventually compute (fidelity, fidelity_label, threat_level,
 * activity_trend, predictive_rationale) has a default that's usually right.
 *
 * "Save & Add Full Detail" saves the minimal row and emits the created id
 * so the parent can pop open the full ThreatActorFormModal pre-populated.
 * (Currently: just emits done — full-detail follow-up is E14 §2.3's nice-to-
 * have, not the acceptance bar. Ship the fast path; iterate on polish.)
 */
import { ref, onMounted, nextTick } from 'vue';
import apiClient from '@/api/client';
import QuickAddScopeLabel from './QuickAddScopeLabel.vue';

const emit = defineEmits<{ done: [] }>();

const name = ref('');
const type = ref('cybercriminal');
const country = ref('');
const notes = ref('');

const saving = ref(false);
const errorMsg = ref<string | null>(null);
const lastSavedName = ref<string | null>(null);

const nameInput = ref<HTMLInputElement | null>(null);

onMounted(async () => {
  await nextTick();
  nameInput.value?.focus();
});

async function save(addAnother: boolean) {
  if (!name.value.trim()) return;
  saving.value = true;
  errorMsg.value = null;

  try {
    await apiClient.post('/threats', {
      name: name.value.trim(),
      type: type.value,
      // Defaults the algorithm will later compute; Oweis can tune later via
      // the full edit modal.
      sophistication: 'moderate',
      resourceLevel: 'team',
      primaryMotivation: type.value === 'nation-state' ? 'espionage' : 'financial',
      fidelity: 50,
      fidelityLabel: 'Possible',
      predictedThreatLevel: 'medium',
      predictiveRationale: notes.value.trim() || 'Pending analyst review',
      activityTrend: 'stable',
      countryOfOrigin: country.value.trim() || undefined,
      strategy: notes.value.trim() || undefined,
    });

    if (addAnother) {
      lastSavedName.value = name.value.trim();
      name.value = '';
      country.value = '';
      notes.value = '';
      await nextTick();
      nameInput.value?.focus();
    } else {
      emit('done');
    }
  } catch (err: unknown) {
    const resp = (err as { response?: { data?: { message?: string | string[] } } })?.response;
    const m = resp?.data?.message;
    errorMsg.value = Array.isArray(m) ? m[0] : m ?? 'Save failed';
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
    e.preventDefault();
    save(e.shiftKey);
  }
}
</script>

<template>
  <form
    class="quick-add-actor-form flex flex-col gap-3"
    data-qa-form="actor"
    @keydown="onKeydown"
    @submit.prevent="save(false)"
  >
    <div class="quick-add-actor-header flex flex-col gap-1">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-text-primary">Add Threat Actor</h2>
        <QuickAddScopeLabel />
      </div>
      <p v-if="lastSavedName" class="text-xs text-status-success">Saved: {{ lastSavedName }}</p>
    </div>

    <div v-if="errorMsg" class="text-sm text-status-error">{{ errorMsg }}</div>

    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Name</label>
        <input
          ref="nameInput"
          v-model="name"
          class="qa-input"
          placeholder="APT29, LockBit, Scattered Spider"
          data-qa-field="name"
          autocomplete="off"
        />
      </div>

      <div class="grid grid-cols-2 gap-2">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-text-muted">Type</label>
          <select v-model="type" class="qa-input" data-qa-field="type">
            <option value="nation-state">Nation State</option>
            <option value="apt">APT</option>
            <option value="cybercriminal">Cybercriminal</option>
            <option value="ransomware">Ransomware</option>
            <option value="hacktivist">Hacktivist</option>
            <option value="insider-threat">Insider Threat</option>
            <option value="unattributed">Unattributed</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-text-muted">Country (ISO)</label>
          <input
            v-model="country"
            class="qa-input"
            placeholder="RU, CN, KP…"
            maxlength="5"
            data-qa-field="country"
            autocomplete="off"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs text-text-muted">Notes / Rationale (optional)</label>
        <textarea
          v-model="notes"
          class="qa-input"
          rows="2"
          placeholder="Why you're tracking them"
          data-qa-field="notes"
        />
      </div>
    </div>

    <div class="flex items-center justify-end gap-2 pt-2">
      <button type="button" class="qa-btn-ghost" @click="emit('done')">Cancel</button>
      <button
        type="button"
        class="qa-btn-secondary"
        :disabled="!name.trim() || saving"
        data-qa-action="save-and-add"
        @click="save(true)"
      >
        Save &amp; Add Another
      </button>
      <button
        type="submit"
        class="qa-btn-primary"
        :disabled="!name.trim() || saving"
        data-qa-action="save"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.qa-input {
  background: var(--color-surface-700, #2a2a3e);
  border: 1px solid var(--color-border-default, #3a3a4e);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: var(--color-text-primary, #e5e5ea);
  font-size: 0.875rem;
  outline: none;
}
.qa-input:focus {
  border-color: var(--color-accent-blue, #3b82f6);
}
.qa-btn-primary {
  background: var(--color-accent-blue, #3b82f6);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}
.qa-btn-primary:hover:not(:disabled) { opacity: 0.9; }
.qa-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.qa-btn-secondary {
  background: var(--color-surface-700, #2a2a3e);
  color: var(--color-text-primary, #e5e5ea);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid var(--color-border-default, #3a3a4e);
}
.qa-btn-secondary:hover:not(:disabled) { background: var(--color-surface-600, #3a3a4e); }
.qa-btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
.qa-btn-ghost {
  color: var(--color-text-secondary, #a0a0aa);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
}
.qa-btn-ghost:hover { background: var(--color-surface-700, #2a2a3e); }
</style>
